import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Copy, ExternalLink, Save, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";

const EMPTY = {
  name: "", list_id: "", affiliate_url: "", webhook_url: "", notes: "",
  ga4_measurement_id: "", meta_pixel_id: "", custom_head_code: "", is_active: true,
  // Phase 1 additions
  email: "", phone: "",
  master_campaign_id: "",
  everflow_postback_url_template: "",
  whatsapp_group_invite_url: "",
  whatsapp_reminder_days: 3,
  revshare_percent: 0,
};

export default function AdminAffiliates() {
  const [list, setList] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([api.get("/affiliates"), api.get("/campaigns")]);
      setList(a.data);
      setCampaigns(c.data);
    } catch (_) {
      toast.error("שגיאה בטעינה");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name,
      list_id: a.list_id,
      affiliate_url: a.affiliate_url,
      webhook_url: a.webhook_url || "",
      notes: a.notes || "",
      is_active: a.is_active,
      ga4_measurement_id: a.ga4_measurement_id || "",
      meta_pixel_id: a.meta_pixel_id || "",
      custom_head_code: a.custom_head_code || "",
      email: a.email || "",
      phone: a.phone || "",
      master_campaign_id: a.master_campaign_id || "",
      everflow_postback_url_template: a.everflow_postback_url_template || "",
      whatsapp_group_invite_url: a.whatsapp_group_invite_url || "",
      whatsapp_reminder_days: a.whatsapp_reminder_days ?? 3,
      revshare_percent: a.revshare_percent ?? 0,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.list_id || !form.affiliate_url) {
      toast.error("יש למלא שם, list_id ו-affiliate_url");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        webhook_url: form.webhook_url || null,
        notes: form.notes || null,
        master_campaign_id: form.master_campaign_id || null,
        whatsapp_reminder_days: Math.max(0, Number(form.whatsapp_reminder_days || 0)),
        revshare_percent: Math.max(0, Math.min(100, Number(form.revshare_percent || 0))),
      };
      if (editing) {
        await api.put(`/affiliates/${editing.id}`, payload);
        toast.success("נשמר!");
      } else {
        await api.post("/affiliates", payload);
        toast.success("האפיליאייט נוצר!");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאה");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("למחוק את האפיליאייט?")) return;
    try {
      await api.delete(`/affiliates/${id}`);
      toast.success("נמחק");
      load();
    } catch (_) {
      toast.error("שגיאה");
    }
  };

  const defaultCampaign = campaigns.find((c) => c.is_default) || campaigns[0];

  const buildLink = (aff) => {
    // Two flavors: if the affiliate has a master_campaign_id, link directly to it
    // and include ?sub1=<slug> so Everflow attribution kicks in. Otherwise fall
    // back to the previous ?list=<slug> behavior.
    const base = window.location.origin;
    const master = aff?.master_campaign_id ? campaigns.find((c) => c.id === aff.master_campaign_id) : null;
    const targetSlug = master?.slug || defaultCampaign?.slug;
    const params = new URLSearchParams({ sub1: aff.list_id });
    return targetSlug ? `${base}/c/${targetSlug}?${params.toString()}` : `${base}/?${params.toString()}`;
  };

  const buildDashboardLink = (aff) => {
    if (!aff.dashboard_magic_token) return null;
    return `${window.location.origin}/affiliate?token=${aff.dashboard_magic_token}`;
  };

  const copyLink = (aff) => {
    navigator.clipboard.writeText(buildLink(aff));
    toast.success("הקישור הועתק");
  };

  const copyDashLink = (aff) => {
    const link = buildDashboardLink(aff);
    if (!link) {
      toast.error("אין Magic Token לאפיליאייט זה");
      return;
    }
    navigator.clipboard.writeText(link);
    toast.success("קישור הדשבורד הועתק");
  };

  return (
    <AdminLayout title="אפיליאייטים">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 text-sm leading-relaxed">
        <strong>איך זה עובד:</strong> כל אפיליאייט מקבל <code className="bg-white px-1 rounded">list_id</code> ייחודי וקישור Affiliate ייחודי.
        העתיקו את "קישור התנועה" ושלחו לאפיליאייט – הלידים שמגיעים דרכו ירוטטו אוטומטית לקישור הייחודי שלו בסיום המשפך.
      </div>

      <div className="flex justify-between items-center mb-6">
        <p className="text-stone-600 text-sm">סך הכל {list.length} אפיליאייטים פעילים.</p>
        <Button onClick={openNew} data-testid="new-affiliate-btn" className="bg-brand-amber hover:bg-brand-amberHover rounded-full h-11 font-bold">
          <Plus className="w-4 h-4 me-2" />
          אפיליאייט חדש
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
          <p className="text-stone-500 mb-4">אין אפיליאייטים עדיין.</p>
          <Button onClick={openNew} className="bg-brand-amber hover:bg-brand-amberHover rounded-full">צרו אפיליאייט ראשון</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="affiliates-list">
          {list.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl p-5 border border-stone-200 hover:shadow-lg transition" data-testid={`affiliate-${a.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-[800] text-lg truncate">{a.name}</h3>
                  <code className="text-xs text-brand-amber bg-amber-50 px-2 py-0.5 rounded">list={a.list_id}</code>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${a.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                  {a.is_active ? "פעיל" : "כבוי"}
                </span>
              </div>

              <div className="bg-stone-50 rounded-xl p-3 mb-3">
                <div className="text-xs text-stone-500 mb-1">קישור Affiliate שלו (יוצג בסוף המשפך):</div>
                <div className="text-xs ltr break-all font-bold text-brand-charcoal">{a.affiliate_url}</div>
              </div>

              <div className="bg-emerald-50 rounded-xl p-3 mb-2 border border-emerald-200">
                <div className="text-xs text-emerald-700 font-bold mb-1">קישור התנועה (עם ?sub1):</div>
                <div className="text-xs ltr break-all font-mono">{buildLink(a)}</div>
              </div>

              {a.dashboard_magic_token && (
                <div className="bg-blue-50 rounded-xl p-3 mb-4 border border-blue-200">
                  <div className="text-xs text-blue-700 font-bold mb-1">קישור דשבורד אפיליאייט:</div>
                  <div className="text-xs ltr break-all font-mono">{buildDashboardLink(a)}</div>
                </div>
              )}

              {a.notes && (
                <p className="text-xs text-stone-500 mb-3 line-clamp-2">{a.notes}</p>
              )}

              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={() => copyLink(a)} className="flex-1 rounded-full h-9 text-xs" data-testid={`copy-link-${a.id}`}>
                  <Copy className="w-3 h-3 me-1" /> העתק תנועה
                </Button>
                {a.dashboard_magic_token && (
                  <Button variant="outline" onClick={() => copyDashLink(a)} className="rounded-full h-9 px-3 text-xs" data-testid={`copy-dash-${a.id}`}>
                    <Link2 className="w-3 h-3 me-1" /> דשבורד
                  </Button>
                )}
                <a href={buildLink(a)} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full h-9 px-3 text-xs"><ExternalLink className="w-3 h-3" /></Button>
                </a>
                <Button variant="outline" onClick={() => openEdit(a)} data-testid={`edit-affiliate-${a.id}`} className="rounded-full h-9 px-3 text-xs">
                  <Edit className="w-3 h-3" />
                </Button>
                <Button variant="outline" onClick={() => remove(a.id)} className="rounded-full h-9 px-3 text-xs text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="affiliate-dialog">
          <DialogHeader>
            <DialogTitle className="font-[800] text-2xl">{editing ? "עריכת אפיליאייט" : "אפיליאייט חדש"}</DialogTitle>
            <DialogDescription className="text-stone-500">
              הגדירו list_id ייחודי + קישור Affiliate. הלידים שמגיעים דרך <code className="bg-white px-1 rounded ltr text-xs">?sub1={form.list_id || "&lt;list_id&gt;"}</code> מקבלים routing למשפך המאסטר של האפיליאייט.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* --- BASIC INFO --- */}
            <div className="border border-stone-200 rounded-2xl p-4 space-y-3">
              <h4 className="font-[800] text-sm">פרטים בסיסיים</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-bold mb-1.5 block">שם האפיליאייט</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="יוסי כהן"
                    data-testid="aff-form-name"
                    className="h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold mb-1.5 block">List ID / slug (משמש כ-<span className="ltr text-brand-amber">sub1</span>)</Label>
                  <Input
                    value={form.list_id}
                    onChange={(e) => setForm((f) => ({ ...f, list_id: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "") }))}
                    placeholder="yossi_fb"
                    data-testid="aff-form-list-id"
                    className="h-11 ltr text-left font-mono"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-bold mb-1.5 block">אימייל</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="yossi@example.com"
                    data-testid="aff-form-email"
                    className="h-11 ltr text-left"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold mb-1.5 block">טלפון</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+972501234567"
                    data-testid="aff-form-phone"
                    className="h-11 ltr text-left"
                  />
                </div>
              </div>
            </div>

            {/* --- MASTER CAMPAIGN --- */}
            <div className="border border-stone-200 rounded-2xl p-4 space-y-3">
              <h4 className="font-[800] text-sm">משפך מאסטר</h4>
              <p className="text-xs text-stone-500 leading-[1.6] -mt-1">
                בחר את המשפך שהאפיליאייט מקדם. עדכונים למשפך המאסטר משפיעים אוטומטית על כל האפיליאייטים המחוברים אליו — ללא שכפולים.
              </p>
              <div>
                <Label className="text-sm font-bold mb-1.5 block">משפך מאסטר</Label>
                <select
                  value={form.master_campaign_id || ""}
                  onChange={(e) => setForm((f) => ({ ...f, master_campaign_id: e.target.value }))}
                  data-testid="aff-form-master-campaign"
                  className="h-11 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                >
                  <option value="">— (משתמש בברירת מחדל של המערכת) —</option>
                  {campaigns.filter((c) => c.is_master_template || c.is_default).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.brand_name || c.slug} — /c/{c.slug}
                      {c.is_master_template ? " ★" : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-stone-500 mt-1">מוצגים רק קמפיינים המסומנים "משפך מאסטר" או ברירת מחדל. סמן קמפיינים ב-Campaign Builder.</p>
              </div>
              <div>
                <Label className="text-sm font-bold mb-1.5 block">Affiliate URL (fallback ישן — יופיע בסוף המשפך אם לא הוגדר בקמפיין)</Label>
                <Input
                  value={form.affiliate_url}
                  onChange={(e) => setForm((f) => ({ ...f, affiliate_url: e.target.value }))}
                  placeholder="https://partner.com/quiz?ref=yossi"
                  data-testid="aff-form-affiliate-url"
                  className="h-11 ltr text-left"
                />
              </div>
            </div>

            {/* --- EVERFLOW --- */}
            <div className="border border-stone-200 rounded-2xl p-4 space-y-3">
              <h4 className="font-[800] text-sm flex items-center gap-2">
                <span>Everflow Postback</span>
                <code className="text-[10px] bg-amber-50 text-brand-amber px-1.5 py-0.5 rounded border border-amber-200 ltr">first click only</code>
              </h4>
              <p className="text-xs text-stone-500 leading-[1.6] -mt-1">
                URL של Everflow (של האפיליאייט הזה) עם placeholders. יידחף פעם אחת בלבד — בלחיצה הראשונה של הליד על לינק ההצעה.<br/>
                Placeholders זמינים: <code className="ltr text-[10px] bg-stone-100 px-1 rounded">{"{TXN_ID}"}</code> <code className="ltr text-[10px] bg-stone-100 px-1 rounded">{"{AFF_SLUG}"}</code> <code className="ltr text-[10px] bg-stone-100 px-1 rounded">{"{LEAD_ID}"}</code> <code className="ltr text-[10px] bg-stone-100 px-1 rounded">{"{LINK_KEY}"}</code> <code className="ltr text-[10px] bg-stone-100 px-1 rounded">{"{TEMPLATE_ID}"}</code> <code className="ltr text-[10px] bg-stone-100 px-1 rounded">{"{UTM_SOURCE}"}</code> <code className="ltr text-[10px] bg-stone-100 px-1 rounded">{"{CLICKED_AT}"}</code>
              </p>
              <Textarea
                value={form.everflow_postback_url_template}
                onChange={(e) => setForm((f) => ({ ...f, everflow_postback_url_template: e.target.value }))}
                data-testid="aff-form-everflow-postback"
                rows={3}
                placeholder={"https://tracking.everflow.io/postback/?nid=1&transaction_id={TXN_ID}&sub1={AFF_SLUG}&sub2={LEAD_ID}&sub3={LINK_KEY}"}
                className="ltr text-left font-mono text-xs"
              />
              <div>
                <Label className="text-sm font-bold mb-1.5 block">Revshare % (עמלה מההכנסות)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  value={form.revshare_percent}
                  onChange={(e) => setForm((f) => ({ ...f, revshare_percent: e.target.value }))}
                  data-testid="aff-form-revshare"
                  placeholder="30"
                  className="h-11 ltr text-left"
                />
                <p className="text-[11px] text-stone-500 mt-1">0-100. משמש לחישוב Revshare בייבוא CSV מ-Everflow. 0 = אין revshare.</p>
              </div>
            </div>

            {/* --- WHATSAPP GROUP --- */}
            <div className="border border-stone-200 rounded-2xl p-4 space-y-3">
              <h4 className="font-[800] text-sm">קבוצת WhatsApp של האפיליאייט</h4>
              <p className="text-xs text-stone-500 leading-[1.6] -mt-1">
                Admin יוצר את הקבוצה ידנית ב-Meta Business Manager ומדביק כאן את קישור ההזמנה. אם ריק — welcome נשלח בלי הזמנה לקבוצה.
              </p>
              <Input
                value={form.whatsapp_group_invite_url}
                onChange={(e) => setForm((f) => ({ ...f, whatsapp_group_invite_url: e.target.value }))}
                data-testid="aff-form-wa-group-invite"
                placeholder="https://chat.whatsapp.com/XXXXXXXXX"
                className="h-11 ltr text-left"
              />
              <div>
                <Label className="text-sm font-bold mb-1.5 block">תזכורת אחרי כמה ימים ללידים שלא נכנסו?</Label>
                <Input
                  type="number"
                  min={0}
                  max={30}
                  value={form.whatsapp_reminder_days}
                  onChange={(e) => setForm((f) => ({ ...f, whatsapp_reminder_days: e.target.value }))}
                  data-testid="aff-form-wa-reminder-days"
                  placeholder="3"
                  className="h-11 ltr text-left w-32"
                />
                <p className="text-[11px] text-stone-500 mt-1">0 = ללא תזכורת. אחרת יישלח template תזכורת אם הליד לא לחץ על הזמנת הקבוצה.</p>
              </div>
            </div>

            {/* --- WEBHOOK / NOTES --- */}
            <div>
              <Label className="text-sm font-bold mb-1.5 block">Webhook URL (אופציונלי – יעקוף את ה-webhook של הקמפיין)</Label>
              <Input
                value={form.webhook_url}
                onChange={(e) => setForm((f) => ({ ...f, webhook_url: e.target.value }))}
                placeholder="https://api.crm.com/leads"
                className="h-11 ltr text-left"
              />
            </div>
            <div>
              <Label className="text-sm font-bold mb-1.5 block">הערות פנימיות</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="מקור התנועה, תנאי עמלה וכו'"
                rows={2}
              />
            </div>

            {/* Tracking codes per-affiliate */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="font-[800] mb-1">קודי מעקב ופיקסלים</h4>
              <p className="text-xs text-stone-500 leading-[1.6] mb-3">
                נטענים אוטומטית כשגולש מגיע עם <code className="bg-stone-100 px-1 rounded ltr">?list={form.list_id || "<list_id>"}</code>. כפופים להסכמת המשתמש.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-bold mb-1.5 block">GA4 Measurement ID</Label>
                  <Input
                    value={form.ga4_measurement_id}
                    onChange={(e) => setForm((f) => ({ ...f, ga4_measurement_id: e.target.value }))}
                    data-testid="aff-form-ga4"
                    placeholder="G-XXXXXXXXXX"
                    className="h-10 ltr text-left font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold mb-1.5 block">Meta Pixel ID</Label>
                  <Input
                    value={form.meta_pixel_id}
                    onChange={(e) => setForm((f) => ({ ...f, meta_pixel_id: e.target.value }))}
                    data-testid="aff-form-meta-pixel"
                    placeholder="1234567890123456"
                    className="h-10 ltr text-left font-mono text-sm"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-sm font-bold mb-1.5 block">Custom Code (HTML/JS)</Label>
                <Textarea
                  value={form.custom_head_code}
                  onChange={(e) => setForm((f) => ({ ...f, custom_head_code: e.target.value }))}
                  data-testid="aff-form-custom-code"
                  rows={4}
                  placeholder={'<script>// Hotjar / TikTok Pixel וכו\'</script>'}
                  className="ltr text-left font-mono text-xs"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50">
              <Label className="font-bold">אפיליאייט פעיל</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">ביטול</Button>
            <Button onClick={save} disabled={saving} data-testid="aff-save-btn" className="bg-brand-amber hover:bg-brand-amberHover rounded-full font-bold">
              <Save className="w-4 h-4 me-2" />
              {saving ? "שומר..." : editing ? "שמור" : "צור"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
